import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Feather, Mail, MapPin, Phone } from "lucide-react";
import { toast } from "sonner";
import { useSettings } from "@/context/SettingsContext";
import { scrollToTop } from "@/lib/scroll";

const exploreLinks = [
  { label: "Domestic Tours", to: "/packages/domestic" },
  { label: "International Tours", to: "/packages/international" },
  { label: "Visa Services", to: "/packages/visa" },
  { label: "Admin Portal", to: "/admin/login" },
  { label: "DMC Hub", to: "/dmc-hub" },
];

const destinations = [
  { label: "Vietnam", to: "/packages/international?q=Vietnam" },
  { label: "Switzerland", to: "/packages/international?q=Switzerland" },
  { label: "Dubai", to: "/packages/international?q=Dubai" },
  { label: "Bali", to: "/packages/international?q=Bali" },
  { label: "Japan", to: "/packages/international?q=Japan" },
  { label: "Kerala", to: "/packages/domestic?q=Kerala" },
  { label: "Kashmir", to: "/packages/domestic?q=Kashmir" },
  { label: "Rajasthan", to: "/packages/domestic?q=Rajasthan" },
  { label: "Paris", to: "/packages/international?q=Paris" },
  { label: "Singapore", to: "/packages/international?q=Singapore" },
];

export const Footer = () => {
  const [email, setEmail] = useState("");
  const settings = useSettings();
  const brandParts = settings.company_name.split(" ");
  const logoSrc = settings.logo_url ? `${process.env.REACT_APP_BACKEND_URL}${settings.logo_url}` : null;
  const location = useLocation();
  const navigate = useNavigate();

  const goHome = (e) => {
    e.preventDefault();
    if (location.pathname === "/") {
      scrollToTop();
    } else {
      navigate("/", { state: { scrollTo: "__top" } });
    }
  };

  const subscribe = (e) => {
    e.preventDefault();
    if (!email) return;
    toast.success("You're on the list — travel inspiration incoming.");
    setEmail("");
  };

  return (
    <footer data-testid="site-footer" className="border-t border-gold/15 bg-surface/60 px-4 sm:px-8 lg:px-16 pt-16 pb-8">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-14">
          <div className="md:col-span-4">
            <Link to="/" onClick={goHome} data-testid="footer-logo" className="flex items-center gap-2.5 mb-5 w-max">
              {logoSrc ? (
                <img src={logoSrc} alt={settings.company_name} className="h-12 w-auto object-contain bg-white rounded-xl px-2.5 py-1.5" />
              ) : (
                <span className="w-9 h-9 rounded-full border border-gold/40 flex items-center justify-center text-gold">
                  <Feather size={17} strokeWidth={1.75} />
                </span>
              )}
              <span className="font-display text-2xl text-cream">
                {brandParts[0]} <span className="italic text-gold">{brandParts.slice(1).join(" ")}</span>
              </span>
            </Link>
            <p className="text-sm text-faded leading-relaxed max-w-xs mb-6">{settings.tagline}</p>
            <div className="space-y-2.5 text-xs text-faded">
              <p className="flex items-center gap-2"><Phone size={12} className="text-gold" /> {settings.phone}</p>
              <p className="flex items-center gap-2"><Mail size={12} className="text-gold" /> {settings.email}</p>
              <p className="flex items-start gap-2"><MapPin size={12} className="text-gold mt-0.5 shrink-0" /> {settings.address}</p>
            </div>
          </div>

          <div className="md:col-span-2">
            <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-gold mb-5">Explore</p>
            <ul className="space-y-3">
              {exploreLinks.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="text-sm text-mist hover:text-gold transition-colors" data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}>
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-3">
            <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-gold mb-5">Top Destinations</p>
            <div className="flex flex-wrap gap-2">
              {destinations.map((d) => (
                <Link
                  key={d.label}
                  to={d.to}
                  data-testid={`footer-dest-${d.label.toLowerCase()}`}
                  className="text-xs text-mist border border-cream/15 rounded-full px-3.5 py-1.5 hover:border-gold/50 hover:text-gold transition-colors"
                >
                  {d.label}
                </Link>
              ))}
            </div>
          </div>

          <div className="md:col-span-3">
            <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-gold mb-5">Travel Inspiration</p>
            <p className="text-sm text-faded mb-4">Exclusive deals and a dose of wanderlust, monthly.</p>
            <form onSubmit={subscribe} className="flex gap-2" data-testid="newsletter-form">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="newsletter-email-input"
                className="field-input flex-1"
                placeholder="you@example.com"
              />
              <button type="submit" data-testid="newsletter-subscribe-btn" className="bg-gold text-ink text-sm font-semibold px-5 rounded-lg hover:bg-gold-hover transition-colors">
                Join
              </button>
            </form>
          </div>
        </div>

        <div className="border-t border-cream/10 pt-7 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-faded">© 2026 {settings.company_name} Pvt. Ltd. All rights reserved.</p>
          <p className="text-xs font-mono tracking-widest text-faded">DOMESTIC · INTERNATIONAL · VISA</p>
        </div>
      </div>
    </footer>
  );
};
